/*
 * Quick-add bottom sheet behaviour.
 *
 * Previously this ran as an inline <script> inside every product card, so a
 * collection page shipped one near-identical copy per product (~160KB of inline
 * JS on a 10-card grid). It now loads once and initialises every modal on the
 * page, reading its per-product variant data from the JSON blob the card emits.
 */
(() => {
  const INIT_FLAG = 'fsQuickAddReady';

  const formatMoney = (cents, moneyFormat) => {
    const amount = (cents / 100).toFixed(2);
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.floor(cents / 100))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
  };

  const initModal = (modal) => {
    if (!modal || modal.dataset[INIT_FLAG]) return;

    const dataEl = modal.querySelector('[data-quick-add-data]');
    if (!dataEl) return;

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    const variants = data.variants || [];
    const moneyFormat = data.money_format || '{{amount}}';

    const select = modal.querySelector('[data-quick-add-variant]');
    const addBtn = modal.querySelector('[data-quick-add-submit]');
    const addText = addBtn?.querySelector('[data-add-text]');
    const soldOutText = addBtn?.querySelector('[data-sold-out]');
    const btnPrice = addBtn?.querySelector('[data-btn-price]');
    const currentPriceEl = modal.querySelector('[data-current-price]');
    const comparePriceEl = modal.querySelector('[data-compare-price]');
    const qtyWrapper = modal.querySelector('[data-quick-add-qty]');
    const qtyInput = qtyWrapper?.querySelector('input[type="number"]');
    const backdrop = modal.querySelector('[data-backdrop]');
    const dragHandle = modal.querySelector('[data-drag-handle]');
    const content = modal.querySelector('.fs-bottom-sheet__content');

    const updateState = () => {
      if (!select) return;
      const variant = variants.find((v) => v.id == select.value);
      const available = variant ? variant.available : false;

      // Update button state
      if (addBtn) addBtn.disabled = !available;
      if (available) {
        addText?.classList.remove('hidden');
        soldOutText?.classList.add('hidden');
      } else {
        addText?.classList.add('hidden');
        soldOutText?.classList.remove('hidden');
      }

      // Update prices
      if (variant) {
        const price = formatMoney(variant.price, moneyFormat);
        if (currentPriceEl) currentPriceEl.textContent = price;
        if (btnPrice) btnPrice.textContent = price;

        // Handle compare-at price
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          if (comparePriceEl) {
            comparePriceEl.textContent = formatMoney(variant.compare_at_price, moneyFormat);
            comparePriceEl.style.display = '';
          }
        } else {
          if (comparePriceEl) comparePriceEl.style.display = 'none';
        }
      }
    };

    // Swipe to dismiss (mobile)
    let startY = 0;
    let currentY = 0;
    let isDragging = false;

    const handleTouchStart = (e) => {
      startY = e.touches[0].clientY;
      isDragging = true;
      content.style.transition = 'none';
    };

    const handleTouchMove = (e) => {
      if (!isDragging) return;
      currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;
      if (deltaY > 0) {
        content.style.transform = `translateY(${deltaY}px)`;
      }
    };

    const handleTouchEnd = () => {
      if (!isDragging) return;
      isDragging = false;
      content.style.transition = '';
      const deltaY = currentY - startY;
      if (deltaY > 100) {
        modal.querySelector('[id^="ModalClose-"]')?.click();
      } else {
        content.style.transform = '';
      }
      startY = 0;
      currentY = 0;
    };

    dragHandle?.addEventListener('touchstart', handleTouchStart, { passive: true });
    dragHandle?.addEventListener('touchmove', handleTouchMove, { passive: true });
    dragHandle?.addEventListener('touchend', handleTouchEnd);

    // Close on backdrop click
    backdrop?.addEventListener('click', () => {
      modal.querySelector('[id^="ModalClose-"]')?.click();
    });

    // Manual quantity button handling (replaces custom element handler)
    const qtyBtns = modal.querySelectorAll('.fs-bottom-sheet__qty-btn');
    qtyBtns.forEach((btn) => {
      btn.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (!qtyInput) return;
          const current = parseInt(qtyInput.value || '1', 10) || 1;
          const isPlus = btn.getAttribute('name') === 'plus';
          const next = isPlus ? current + 1 : Math.max(1, current - 1);
          qtyInput.value = next;
          qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        },
        true
      );
    });

    select?.addEventListener('change', updateState);
    qtyInput?.addEventListener('change', updateState);
    updateState();

    modal.dataset[INIT_FLAG] = 'true';
  };

  const initAll = (root) => {
    (root || document).querySelectorAll('quick-add-modal[id^="QuickAdd-"]').forEach(initModal);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Cards can arrive after load: section re-render in the theme editor, and
  // paginated / filtered collection grids.
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('fs:products:loaded', (e) => initAll(e.target));
})();
